import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Assignment,
  CloudUpload,
  PlayArrow,
  Refresh,
  Search,
} from '@mui/icons-material';
import { vendorAPI } from '../../services/api';
import { EmptyState, Section, StatusChip, TableShell } from '../shared/ui';
import { tokens } from '../../theme';
import ResumeUploadForm from './ResumeUploadForm';

/**
 * The shared queue of hiring-manager requirements open for sourcing.
 *
 * A requirement is not addressed to one recruiter, so everyone sees the same
 * list. Claiming it ("Start sourcing") is what grants the right to submit
 * candidates against it, which is why the upload action stays disabled until
 * then rather than failing with a permission error at submit time.
 */
const RequirementQueue = ({ onCandidateSubmitted }) => {
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [claiming, setClaiming] = useState(null);
  const [uploadFor, setUploadFor] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await vendorAPI.getRequirements();
      setRequirements(response.data?.requirements || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load requirements.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleClaim = async (requirement) => {
    setClaiming(requirement.id);
    setError(null);
    try {
      await vendorAPI.startSourcing(requirement.id);
      setNotice(`You are now sourcing for ${requirement.requirement_code}.`);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not start sourcing.');
    } finally {
      setClaiming(null);
    }
  };

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return requirements.filter((r) => {
      if (filter === 'mine' && !r.is_mine) return false;
      if (filter === 'unclaimed' && r.is_mine) return false;
      if (!term) return true;
      return [r.role_title, r.requirement_code, r.expertise, r.location, r.manager_name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    });
  }, [requirements, search, filter, ]);

  const formatBudget = (r) => {
    if (r.budget_min == null && r.budget_max == null) return '—';
    const symbols = { INR: '₹', USD: '$', EUR: '€', GBP: '£' };
    const symbol = symbols[r.budget_currency] || `${r.budget_currency} `;
    const fmt = (v) => `${symbol}${Number(v).toLocaleString()}`;
    if (r.budget_min != null && r.budget_max != null) return `${fmt(r.budget_min)} – ${fmt(r.budget_max)}`;
    return r.budget_max != null ? `Up to ${fmt(r.budget_max)}` : `From ${fmt(r.budget_min)}`;
  };

  return (
    <>
      <Section
        title={`Open requirements (${visible.length})`}
        subtitle="Raised by hiring managers. Claim one to start submitting candidates."
        disablePadding
        actions={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search role, code, skills…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: 220 }}
              InputProps={{ startAdornment: <Search sx={{ mr: 1, fontSize: 18, color: tokens.color.inkTertiary }} /> }}
            />
            <FormControl size="small" sx={{ width: 140 }}>
              <Select value={filter} onChange={(e) => setFilter(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="mine">Mine</MenuItem>
                <MenuItem value="unclaimed">Not claimed</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title="Refresh">
              <span>
                <IconButton size="small" onClick={load} disabled={loading}>
                  <Refresh fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        }
      >
        {(error || notice) && (
          <Box sx={{ px: 2, pt: 2 }}>
            <Typography
              variant="body2"
              sx={{ color: error ? tokens.color.danger : tokens.color.success }}
            >
              {error || notice}
            </Typography>
          </Box>
        )}

        {loading && requirements.length === 0 ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
            <CircularProgress size={24} />
          </Box>
        ) : visible.length === 0 ? (
          <EmptyState
            icon={<Assignment />}
            title="Nothing to source right now"
            description="When a hiring manager publishes a requirement it will appear here."
          />
        ) : (
          <TableShell sx={{ border: 'none' }}>
            <Table size="small" sx={{ minWidth: 1080 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Requirement</TableCell>
                  <TableCell>Expertise</TableCell>
                  <TableCell>Experience</TableCell>
                  <TableCell align="center">Openings</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Budget</TableCell>
                  <TableCell align="center">Priority</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Mine</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <Typography variant="subtitle1" noWrap>{r.role_title}</Typography>
                      <Typography variant="caption" sx={{ fontFamily: tokens.font.mono }}>
                        {r.requirement_code}
                      </Typography>
                      {r.manager_name && (
                        <Typography variant="body2" sx={{ color: tokens.color.inkSecondary }} noWrap>
                          {r.manager_name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {(r.skills || []).slice(0, 4).map((skill) => (
                          <Chip key={skill} size="small" variant="outlined" label={skill} />
                        ))}
                        {(r.skills || []).length > 4 && (
                          <Chip size="small" label={`+${r.skills.length - 4}`} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{r.experience_band}</TableCell>
                    <TableCell align="center">{r.headcount}</TableCell>
                    <TableCell>{r.location}</TableCell>
                    <TableCell>{formatBudget(r)}</TableCell>
                    <TableCell align="center">
                      <StatusChip status={r.priority} />
                    </TableCell>
                    <TableCell align="center">
                      <StatusChip status={r.status} />
                    </TableCell>
                    <TableCell align="center">
                      {r.is_mine ? (
                        <Chip
                          size="small"
                          label={r.my_candidate_count ? `${r.my_candidate_count} sent` : 'Claimed'}
                          sx={{ bgcolor: tokens.color.successSoft, color: tokens.color.success }}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ color: tokens.color.inkTertiary }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
                        {!r.is_mine ? (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={
                              claiming === r.id ? <CircularProgress size={14} /> : <PlayArrow fontSize="small" />
                            }
                            disabled={claiming === r.id}
                            onClick={() => handleClaim(r)}
                          >
                            Start sourcing
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<CloudUpload fontSize="small" />}
                            onClick={() => setUploadFor(r)}
                          >
                            Upload resume
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableShell>
        )}
      </Section>

      <ResumeUploadForm
        open={!!uploadFor}
        onClose={() => setUploadFor(null)}
        job={uploadFor ? { id: uploadFor.job_id, title: uploadFor.role_title } : null}
        requirement={uploadFor}
        onSubmitted={(result) => {
          setNotice(result?.message || 'Candidate submitted.');
          load();
          onCandidateSubmitted?.(result);
        }}
      />
    </>
  );
};

export default RequirementQueue;
